import {
  ApiResponse,
  PaginationRequest,
  PaginationRequestModel,
  Participant,
} from '../models';
import http from '../utils/http';

interface BackendAuthToken {
  name: string;
  url: string;
  token: string;
}

const backendTokensStorageKey =
  'admin-backend-auth-tokens';

function getBackendAuthTokens(): BackendAuthToken[] {
  try {
    const stored = localStorage.getItem(
      backendTokensStorageKey,
    );

    if (!stored) {
      return [];
    }

    const parsed = JSON.parse(stored);

    return Array.isArray(parsed)
      ? parsed
      : [];
  } catch {
    return [];
  }
}

function getBackend(
  backendName: string,
): BackendAuthToken {
  const backends =
    getBackendAuthTokens();

  const backend = backends.find(
    (item) =>
      item.name === backendName,
  );

  if (!backend) {
    throw new Error(
      `Backend "${backendName}" was not found in authenticated backends`,
    );
  }

  return backend;
}

function getBackendUrl(
  backend: BackendAuthToken,
  path: string,
): string {
  return `${backend.url.replace(/\/$/, '')}${path}`;
}

function getBackendRequestConfig(
  backendName: string,
  extra: any = {},
) {
  return {
    ...extra,
    backend: backendName,
  };
}

export async function serviceSwapParticipants(
  eventId: number,
  backendName: string,
) {
  const backend =
    getBackend(backendName);

  const response: ApiResponse<any> =
    await http.put(
      getBackendUrl(
        backend,
        `/api/events/swap-participants/id/${eventId}`,
      ),
      {},
      getBackendRequestConfig(
        backendName,
        {
          headers: {
            successAlert: true,
          },
        },
      ),
    );

  return response.data;
}

export async function fetchParticipant(
  id: number,
  backendName: string,
): Promise<Participant> {
  const backend =
    getBackend(backendName);

  const response:
    ApiResponse<Participant> =
    await http.get(
      getBackendUrl(
        backend,
        `/api/events/participant/id/${id}`,
      ),
      getBackendRequestConfig(
        backendName,
      ),
    );

  return {
    ...response.data,
    backendName,
    backendUrl: backend.url,
  } as Participant;
}

// Should this be Partial<ParticipantModel> ?
export interface UpdateParticipantRequest {
  name: string;
}

// TODO: Response Model
export async function updateParticipant(
  id: number,
  data: any,
  backendName: string,
): Promise<any> {
  const backend =
    getBackend(backendName);

  const response: ApiResponse<any> =
    await http.put(
      getBackendUrl(
        backend,
        `/api/events/participant/edit/${id}`,
      ),
      data,
      getBackendRequestConfig(
        backendName,
        {
          headers: {
            'Content-Type':
              'multipart/form-data',
            successAlert: true,
          },
        },
      ),
    );

  return response.data;
}

// TODO: Response Model
export async function deleteParticipant(
  id: number,
  backendName: string,
): Promise<any> {
  const backend =
    getBackend(backendName);

  const response: ApiResponse<any> =
    await http.delete(
      getBackendUrl(
        backend,
        `/api/events/participant/id/${id}`,
      ),
      getBackendRequestConfig(
        backendName,
      ),
    );

  return response.data;
}